/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { addDays, startOfWeek } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

/** All clinic-facing times render in IST regardless of the viewer's locale. */
const CLINIC_TIME_ZONE = 'Asia/Kolkata';

const DAY_KEY_PATTERN = 'yyyy-MM-dd';

/** One day cell in the week grid. */
export interface CalendarDay {
  /** IST calendar date, `yyyy-MM-dd`, used to bucket sessions. */
  readonly key: string;
  /** Midnight (local) for the day — drives the visible header. */
  readonly date: Date;
}

/** The seven days (Mon–Sun) of the week containing `anchor`. */
export function weekDays(anchor: Date): readonly CalendarDay[] {
  const start = startOfWeek(anchor, { weekStartsOn: 1 });
  return Array.from({ length: 7 }, (_, index) => {
    const date = addDays(start, index);
    return { key: formatInTimeZone(date, CLINIC_TIME_ZONE, DAY_KEY_PATTERN), date };
  });
}

/** The IST calendar-date key (`yyyy-MM-dd`) of an ISO instant, for bucketing. */
export function istDayKey(iso: string): string {
  return formatInTimeZone(new Date(iso), CLINIC_TIME_ZONE, DAY_KEY_PATTERN);
}

/** Shift a date by whole weeks (negative = earlier). */
export function addWeeks(date: Date, weeks: number): Date {
  return addDays(date, weeks * 7);
}

/** `540` → `"09:00"` — minute-of-day to a `<input type="time">` value. */
export function minutesToTime(minutes: number): string {
  const hh = Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0');
  const mm = (minutes % 60).toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

/** `"09:00"` → `540` — a `<input type="time">` value to minute-of-day. */
export function timeToMinutes(value: string): number {
  const [hh, mm] = value.split(':');
  return Number(hh) * 60 + Number(mm);
}

/** Format an ISO-8601 timestamp in the clinic's timezone (IST). */
export function formatIst(iso: string, pattern: string): string {
  return formatInTimeZone(new Date(iso), CLINIC_TIME_ZONE, pattern);
}

/** Render an ISO instant as a `datetime-local` input value (IST wall-clock). */
export function toDatetimeLocalIst(iso: string): string {
  return formatInTimeZone(new Date(iso), CLINIC_TIME_ZONE, "yyyy-MM-dd'T'HH:mm");
}
